/**
 * Feature-flag access for the SPA. Reads the typed `features` map off
 * the authenticated session — flags are server-driven, resolved per
 * tenant in `/api/session`, and propagated through `SessionContext`.
 *
 * Public routes (anything rendered outside `<AppShell>`) can't call
 * these hooks — there's no session in scope. Gate public flows
 * server-side instead; the public endpoint returns 404 when the flag
 * is off, and the SPA renders its existing "not found" state.
 */

import {
  useAuthSession,
  useOptionalAuthSession,
} from "@/lib/session-context";
import type { SessionFeatureFlags } from "@/types/generated/SessionFeatureFlags";

/** Stable name of every flag the SPA knows about. Names match the
 *  Rust `TenantFeature` enum's snake_case wire form. */
export type FeatureName = keyof SessionFeatureFlags;

/** Full feature-flag state from the authenticated session. */
export function useFeatures(): SessionFeatureFlags {
  return useAuthSession().features;
}

/** Convenience: is a single named flag enabled? */
export function useFeature(name: FeatureName): boolean {
  return useFeatures()[name];
}

/**
 * Like `useFeature`, but `false` outside the shell instead of throwing.
 *
 * For OPTIONAL chrome — a dashboard widget or a toolbar control that reads a
 * flag only to decide whether to render at all. `useFeature` throwing is
 * right for a component that cannot function without a session; a component
 * whose honest answer outside the shell is "render nothing" should not take
 * its host page down to say so.
 *
 * Do NOT reach for this to paper over a missing provider in a test. If a
 * component genuinely needs the session, wrap the test in `SessionProvider`
 * — silently rendering nothing is the wrong kind of green.
 */
export function useOptionalFeature(name: FeatureName): boolean {
  return useOptionalAuthSession()?.features[name] ?? false;
}
